import { useMemo } from 'react';

import { competencyTransform } from '@backrs_ui/common-ui';

import commonAppEssayBanner from '@backrs_ui/grow-ui/src/assets/images/common-app-essay-guide/common-app-essay-banner.png';
import {
  GuidePageShell,
  GuideSections,
  GuideToolsSection,
} from '@backrs_ui/grow-ui/src/components/GuidePage';
import { LubavButton } from '@backrs_ui/grow-ui/src/components/Lubav/LubavButton';
import { StickerCardId } from '@backrs_ui/grow-ui/src/pages/Home/hooks/stickerCardMapping';
import { selectContentCompetencies } from '@backrs_ui/grow-ui/src/redux/contentCompetency/contentCompetency';
import { useGetContentNuggetsBySlugsQuery } from '@backrs_ui/grow-ui/src/redux/contentNugget/contentNugget.api';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';

import { useAgentContext } from './CommonAppEssayGuidePageContainer.agentContext';
import { FinalRealityCheckSection, PromptsSection } from './components';
import { commonAppEssayContent } from '../content';

const SECTION_IDS = {
  overview: 'common-app-essay-section-overview',
  tools: 'common-app-essay-section-tools',
  prompts: 'common-app-essay-section-prompts',
  finalRealityCheck: 'common-app-essay-section-reality-check',
} as const;

const COMMON_APP_ESSAY_TOOL_ID = 'common_app_essay_guide';

const COMMON_APP_ESSAY_TOOL_IDS: readonly StickerCardId[] = ['essay_ideas'];

const ESSAYS_COMPETENCY_SLUG = 'academics-essays-writing';

const COMMON_APP_ESSAY_NUGGET_SLUGS: readonly string[] = [
  '5-ways-to-improve-your-writing',
  'how-to-perfect-your-essay-writing-skills',
  'write-less-but-say-more',
  'how-to-write-something-truly-beautiful--alain-de-botton',
];

export const CommonAppEssayGuidePageContainer = () => {
  useAgentContext();

  const { data: resourceNuggets } = useGetContentNuggetsBySlugsQuery(COMMON_APP_ESSAY_NUGGET_SLUGS);
  const { contentCompetencies } = useAppSelector(selectContentCompetencies);

  const essaysCompetency = useMemo(
    () => contentCompetencies.find((c) => c.slug === ESSAYS_COMPETENCY_SLUG),
    [contentCompetencies],
  );

  const orderedResources = useMemo(() => {
    const bySlug = new Map(resourceNuggets?.map((nugget) => [nugget.slug, nugget]));
    const nuggets = COMMON_APP_ESSAY_NUGGET_SLUGS.map((slug) => bySlug.get(slug)).filter(
      (nugget): nugget is NonNullable<typeof nugget> => Boolean(nugget),
    );

    return essaysCompetency ? [competencyTransform(essaysCompetency), ...nuggets] : nuggets;
  }, [essaysCompetency, resourceNuggets]);

  const sections = [
    {
      id: SECTION_IDS.prompts,
      title: commonAppEssayContent.promptsSection.title,
      subtitle: commonAppEssayContent.promptsSection.subtitle,
      rightSlot: (
        <LubavButton
          id="common-app-essay-prompts-lubav-btn"
          label="Which prompt fits me?"
          prompt="Based on what you know about me — my background, interests, experiences, and goals — which Common App essay prompt would be the strongest fit for me? Explain why and suggest a specific angle or story I could explore."
        />
      ),
      content: <PromptsSection prompts={commonAppEssayContent.prompts} />,
    },
    {
      id: SECTION_IDS.tools,
      title: commonAppEssayContent.tools.title,
      subtitle: commonAppEssayContent.tools.subtitle,
      rightSlot: (
        <LubavButton
          id="common-app-essay-tools-lubav-btn"
          label="Which tool should I start with?"
          prompt="Which of these tools should I start with to support my Common App essay writing?"
          allowTools={false}
        />
      ),
      content: (
        <GuideToolsSection toolIds={COMMON_APP_ESSAY_TOOL_IDS} extraNuggets={orderedResources} />
      ),
    },
    {
      id: SECTION_IDS.finalRealityCheck,
      title: commonAppEssayContent.finalRealityCheck.title,
      subtitle: undefined,
      rightSlot: (
        <LubavButton
          id="common-app-essay-reality-check-lubav-btn"
          label="Check my essay"
          promptButtonId="common_app_essay_check"
        />
      ),
      content: <FinalRealityCheckSection content={commonAppEssayContent.finalRealityCheck} />,
    },
  ];

  return (
    <GuidePageShell
      toolId={COMMON_APP_ESSAY_TOOL_ID}
      banner={{
        src: commonAppEssayBanner,
        alt: 'Common App Essay Guide',
      }}
    >
      <GuideSections
        header={commonAppEssayContent.header}
        overview={{
          id: SECTION_IDS.overview,
        }}
        sections={sections}
      />
    </GuidePageShell>
  );
};
